import { useEffect, useMemo, useState } from "react";
import { VerifiedCreatorMark } from "../components/VerifiedCreatorMark";
import { NumgnumCard } from "../components/NumgnumCard";
import { demoCards, NUMGNUM_NAMES } from "../lib/cards";
import { hasSupabaseConfiguration, publicApi, supabase, type FounderRecord, type PublicCard, type PublicPlayer } from "../lib/supabase";

type Tab = "players" | "founders";

export function DiscoverPage() {
  const [tab, setTab] = useState<Tab>("players");
  const [query, setQuery] = useState("");
  const [players, setPlayers] = useState<PublicPlayer[]>([]);
  const [selected, setSelected] = useState<PublicPlayer | null>(null);
  const [cards, setCards] = useState<PublicCard[]>(demoCards.map((card) => ({ ...card, username: "BEACHCOMBER" })));
  const [founders, setFounders] = useState<FounderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [hoardIsLive, setHoardIsLive] = useState(false);

  useEffect(() => {
    publicApi.founders().then(setFounders).catch(() => undefined);
  }, []);

  useEffect(() => {
    const username = selected?.username;
    const activeSupabase = supabase;
    if (!activeSupabase || !username) {
      setHoardIsLive(false);
      return;
    }

    let active = true;
    let refreshTimer: number | undefined;
    let refreshInFlight = false;
    let refreshAgain = false;

    const refreshPublicHoard = async () => {
      if (!active) return;
      if (refreshInFlight) {
        refreshAgain = true;
        return;
      }

      refreshInFlight = true;
      try {
        const [freshCards, matchingPlayers] = await Promise.all([
          publicApi.playerCards(username, true),
          publicApi.searchPlayers(username, true),
        ]);
        if (!active) return;

        setCards(freshCards);
        const freshPlayer = matchingPlayers.find(
          (player) => player.username.toLocaleLowerCase() === username.toLocaleLowerCase(),
        );
        if (freshPlayer) {
          setSelected(freshPlayer);
          setPlayers((current) => current.map((player) => (
            player.username.toLocaleLowerCase() === username.toLocaleLowerCase() ? freshPlayer : player
          )));
        }
      } catch {
        if (active) setHoardIsLive(false);
      } finally {
        refreshInFlight = false;
        if (refreshAgain) {
          refreshAgain = false;
          void refreshPublicHoard();
        }
      }
    };

    const scheduleRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => void refreshPublicHoard(), 120);
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };

    const channel = activeSupabase
      .channel(`web-public-hoard:${username}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "web_player_collection_revisions",
          filter: `username=eq.${username}`,
        },
        scheduleRefresh,
      )
      .subscribe((status) => {
        if (!active) return;
        setHoardIsLive(status === "SUBSCRIBED");
        if (status === "SUBSCRIBED") scheduleRefresh();
      });

    window.addEventListener("focus", scheduleRefresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      active = false;
      window.clearTimeout(refreshTimer);
      window.removeEventListener("focus", scheduleRefresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      void activeSupabase.removeChannel(channel);
    };
  }, [selected?.username]);

  async function search() {
    setLoading(true);
    try {
      const rows = await publicApi.searchPlayers(query);
      setPlayers(rows);
      setSelected(null);
    } finally { setLoading(false); }
  }

  async function choosePlayer(player: PublicPlayer) {
    setSelected(player);
    setLoading(true);
    try {
      const rows = await publicApi.playerCards(player.username);
      setCards(rows);
    } finally { setLoading(false); }
  }

  const publicCards = useMemo(() => cards, [cards]);
  const founderName = (level: number) => NUMGNUM_NAMES[level] ?? `NUMGNUM ${level}`;
  return (
    <div className="records-page page-scene">
      <div className="scene-heading">
        <p className="eyebrow">The living world index</p>
        <h1>Count every creature. Blame every founder.</h1>
        <p>Every serial has receipts. The ledger keeps the whole island caught in 4K.</p>
      </div>
      <div className="record-tabs" role="tablist">
        {(["players", "founders"] as Tab[]).map((value) => <button type="button" key={value} className={tab === value ? "is-active" : ""} onClick={() => setTab(value)}>{value}</button>)}
      </div>
      {!hasSupabaseConfiguration && <div className="demo-ribbon">WORLD RECORDS ARE OFFLINE · TRY AGAIN SOON</div>}
      {tab === "players" && (
        <section className="player-search">
          <form onSubmit={(event) => { event.preventDefault(); void search(); }}>
            <label htmlFor="player-query">Find a player by island name</label>
            <div><input id="player-query" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Beachcomber" /><button type="submit">Search the shoreline</button></div>
          </form>
          <div className="player-results">
            {players.map((player) => <button key={player.username} type="button" onClick={() => void choosePlayer(player)}><strong>{player.username}<VerifiedCreatorMark username={player.username} /></strong><span>{player.wins} W · {player.draws} D · {player.losses} L</span><em>{player.card_count} cards</em></button>)}
          </div>
          {(selected || !hasSupabaseConfiguration) && (
            <div className="public-hoard">
              <div><p className="eyebrow">{selected?.username ?? "Beachcomber"}'s public hoard {selected && <span className={hoardIsLive ? "live-hoard-status is-live" : "live-hoard-status"}>{hoardIsLive ? "LIVE" : "CONNECTING"}</span>}</p><h2>{selected ? `${selected.wins} wins · ${selected.founder_count} founder cards` : "Tap any card to inspect both sides"}</h2></div>
              <div className="public-card-grid">{publicCards.map((card, index) => <NumgnumCard key={`${card.level}-${card.edition}-${card.serial_number}-${index}`} level={card.level} edition={card.edition} shiny={card.is_shiny} founder={card.is_founder} serial={card.serial_number} founderName={card.founder_username} showsSerialBadge />)}</div>
            </div>
          )}
          {loading && <span className="loading-anvil">THE ANVIL IS THINKING…</span>}
        </section>
      )}
      {tab === "founders" && <section className="founder-ledger">{founders.length ? founders.map((row) => {
        const name = founderName(row.level);
        return <article key={`${row.level}-${row.edition}`}><NumgnumCard level={row.level} edition={row.edition} founder shiny={row.is_shiny} serial={row.serial_number} founderName={row.founder_username} showsSerialBadge /><div><span>{row.edition}</span><strong className={name.length > 10 ? "is-long" : ""}>{name}</strong><p>Founded forever by <b>{row.founder_username}</b></p></div></article>;
      }) : <EmptyRecord text="No founder marks have reached the ledger yet." />}</section>}
    </div>
  );
}

function EmptyRecord({ text }: { text: string }) { return <div className="empty-record"><img src="/art/brand/grinning-forge-emblem.webp" alt="" /><p>{text}</p></div>; }
